import { Controller } from "@hotwired/stimulus"

// How often the client re-arms the server-side presence marker. Must stay
// comfortably under AdminAnalyticsChannel::SUBSCRIBER_PRESENCE_TTL (90s) so
// the marker never expires while this dashboard is still connected.
const HEARTBEAT_INTERVAL_MS = 30000

// Connects to data-controller="analytics-dashboard"
export default class extends Controller {
  static targets = [
    "activeUsersList",
    "recentActivity",
    "totalPageViews",
    "totalRentalViews",
    "totalUniqueVisitors",
    "totalActiveUsers",
    "totalActiveCustomers",
    "totalActiveStaff",
    "autoRefreshToggle",
    "refreshCountdown",
    "refreshButton"
  ]

  static values = {
    refreshInterval: { type: Number, default: 60 }, // Refresh interval in seconds
    autoRefresh: { type: Boolean, default: false }  // Auto-refresh disabled by default
  }

  connect() {
    console.log("Analytics Dashboard controller connected");
    this.setupSubscription();
    this.remainingTime = this.refreshIntervalValue;
    this.updateCountdownDisplay();
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.stopRefreshTimer();
    clearInterval(this.heartbeatInterval);
  }

  setupSubscription() {
    this.subscription = window.App.cable.subscriptions.create(
      { channel: "AdminAnalyticsChannel" },
      {
        connected: () => {
          console.log("Connected to Admin Analytics Channel");

          this.heartbeatInterval = setInterval(() => this.subscription.perform("heartbeat"), HEARTBEAT_INTERVAL_MS);
        },
        disconnected: () => {
          console.log("Disconnected from Admin Analytics Channel");

          clearInterval(this.heartbeatInterval);
        },
        received: (data) => {
          console.log("Received data:", data);
          this.updateCounters(data);
          this.updateActiveUsersList(data.active_user_details);
        }
      }
    );
  }

  updateCounters(data) {
    // Update page view counters
    if (this.hasTotalPageViewsTarget) {
      this.totalPageViewsTarget.textContent = data.total_page_views;
    }
    if (this.hasTotalRentalViewsTarget) {
      this.totalRentalViewsTarget.textContent = data.total_rental_views;
    }
    if (this.hasTotalUniqueVisitorsTarget) {
      this.totalUniqueVisitorsTarget.textContent = data.total_unique_visitors;
    }

    // Update active user counters
    if (this.hasTotalActiveUsersTarget) {
      this.totalActiveUsersTarget.textContent = data.active_users;
    }
    if (this.hasTotalActiveCustomersTarget) {
      this.totalActiveCustomersTarget.textContent = data.active_customers;
    }
    if (this.hasTotalActiveStaffTarget) {
      this.totalActiveStaffTarget.textContent = data.active_staff;
    }
  }

  updateActiveUsersList(activeUsers) {
    if (!this.hasActiveUsersListTarget || !activeUsers) return;
    
    // Clear existing content
    this.activeUsersListTarget.innerHTML = '';
    
    if (activeUsers.length === 0) {
      const emptyRow = document.createElement('tr');
      emptyRow.innerHTML = `
        <td colspan="4" class="py-4 text-center text-gray-500">No active users</td>
      `;
      this.activeUsersListTarget.appendChild(emptyRow);
      return;
    }
    
    // Add each active user to the list
    activeUsers.forEach(user => {
      const row = document.createElement('tr');
      row.className = 'border-t';
      
      // Format the role badge based on role
      let roleClass;
      let roleText = user.role.charAt(0).toUpperCase() + user.role.slice(1);
      
      switch (user.role) {
        case 'admin':
          roleClass = 'bg-red-100 text-red-800';
          break;
        case 'manager':
          roleClass = 'bg-purple-100 text-purple-800';
          break;
        case 'staff':
          roleClass = 'bg-blue-100 text-blue-800';
          break;
        case 'customer':
          roleClass = 'bg-green-100 text-green-800';
          break;
        default:
          roleClass = 'bg-gray-100 text-gray-800';
      }
      
      // Calculate time ago
      const lastActiveDate = new Date(user.last_seen_at);
      const timeAgo = this.formatTimeAgo(lastActiveDate);
      const lastActiveDisplay = timeAgo ? `${timeAgo} ago` : "Recently active";
      
      // Get user initials
      const nameParts = user.name.split(' ');
      const initials = nameParts.length > 1 
        ? (nameParts[0].charAt(0) + nameParts[1].charAt(0)).toUpperCase()
        : nameParts[0].charAt(0).toUpperCase();
      
      row.innerHTML = `
        <td class="py-2 px-4 text-sm">
          <div class="flex items-center">
            <div class="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 mr-3">
              ${initials}
            </div>
            <span>${user.name}</span>
          </div>
        </td>
        <td class="py-2 px-4 text-sm">${user.email}</td>
        <td class="py-2 px-4 text-sm">
          <span class="px-2 py-1 text-xs rounded-full ${roleClass}">${roleText}</span>
        </td>
        <td class="py-2 px-4 text-sm">${lastActiveDisplay}</td>
      `;
      
      this.activeUsersListTarget.appendChild(row);
    });
  }
  
  formatTimeAgo(date) {
    if (!date || isNaN(date.getTime())) {
      return null;
    }

    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) {
      return `${diffInSeconds} second${diffInSeconds !== 1 ? 's' : ''}`;
    }
    
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''}`;
    }
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''}`;
    }
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} day${diffInDays !== 1 ? 's' : ''}`;
  }

  addPageView(data) {
    if (!this.hasRecentActivityTarget) return
    
    // Limit to 5 items
    const items = this.recentActivityTarget.querySelectorAll('tr')
    if (items.length >= 5) {
      items[items.length - 1].remove()
    }

    // Format the time
    const viewedAt = new Date(data.viewed_at)
    const timeAgo = this.formatTimeAgo(viewedAt)

    // Create a new row
    const tr = document.createElement('tr')
    tr.className = 'border-b'
    tr.innerHTML = `
      <td class="py-2 px-4">${data.page}</td>
      <td class="py-2 px-4">${data.user || 'Anonymous'}</td>
      <td class="py-2 px-4">${timeAgo}</td>
    `
    
    // Add to the top of the list
    this.recentActivityTarget.prepend(tr)
  }

  // Toggle auto-refresh functionality
  toggleAutoRefresh() {
    this.autoRefreshValue = !this.autoRefreshValue;
    
    if (this.autoRefreshValue) {
      this.startRefreshTimer();
      if (this.hasAutoRefreshToggleTarget) {
        this.autoRefreshToggleTarget.textContent = "Disable Auto-Refresh";
        this.autoRefreshToggleTarget.classList.remove("bg-blue-500");
        this.autoRefreshToggleTarget.classList.add("bg-red-500");
      }
    } else {
      this.stopRefreshTimer();
      if (this.hasAutoRefreshToggleTarget) {
        this.autoRefreshToggleTarget.textContent = "Enable Auto-Refresh";
        this.autoRefreshToggleTarget.classList.remove("bg-red-500");
        this.autoRefreshToggleTarget.classList.add("bg-blue-500");
      }
    }
  }

  // Start the refresh timer
  startRefreshTimer() {
    this.stopRefreshTimer(); // Clear any existing timer
    this.remainingTime = this.refreshIntervalValue;
    this.updateCountdownDisplay();
    
    this.refreshTimer = setInterval(() => {
      this.remainingTime -= 1;
      
      if (this.remainingTime <= 0) {
        this.refreshDashboard();
        this.remainingTime = this.refreshIntervalValue;
      }
      
      this.updateCountdownDisplay();
    }, 1000);
  }

  // Stop the refresh timer
  stopRefreshTimer() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  // Update the countdown display
  updateCountdownDisplay() {
    if (this.hasRefreshCountdownTarget) {
      if (this.autoRefreshValue) {
        this.refreshCountdownTarget.textContent = `Refreshing in ${this.remainingTime}s`;
        this.refreshCountdownTarget.classList.remove("hidden");
      } else {
        this.refreshCountdownTarget.classList.add("hidden");
      }
    }
  }

  // Manually refresh the dashboard
  refreshDashboard() {
    if (this.hasRefreshButtonTarget) {
      this.refreshButtonTarget.classList.add("animate-spin");
    }
    
    // Use Turbo to refresh the page
    Turbo.visit(window.location.href, { action: "replace" });
  }
}
